import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Progress } from './ui/progress';
import { ChevronRight } from 'lucide-react';
import { fetchDashboardJobs, JobStage } from '@/lib/dashboard-api';
import { useUser } from '@/contexts/UserContext';
import { GenericTabBar } from '@/styles/GenericTabBar';
import { GenericTag } from '@/styles/GenericTag';
import '@/design-system/typography.css';
import '@/design-system/forms.css';

const formatSize = (value?: string | number) =>
  typeof value === "number" ? `${value}px` : value;

type DerivedStatus = JobStage;

interface ProjectDisplayItem {
  id: string;
  name: string;
  client: string;
  progressPct: number;
  daysLeft: number;
  budgetText: string;
  location: string;
  badgeStatus: string; // actual status from DB
  derivedStatus: DerivedStatus;
}

export function JobsList() {
  const { companyId } = useUser();
  const [activeTab, setActiveTab] = useState<DerivedStatus>('In Progress');
  const [items, setItems] = useState<ProjectDisplayItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const currency = useMemo(() => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }), []);

  // Scroll container width constraint (matches GenericTable pattern)
  // calc(100vw - 19.2rem - 3rem) accounts for:
  // - Sidebar width (16rem) + spacing (3.2rem) = 19.2rem
  // - Card padding (p-6 = 1.5rem per side = 3rem total)
  const scrollContainerMaxWidth = "calc(100vw - 22.2rem)";
  const scrollMaxWidthValue = formatSize(scrollContainerMaxWidth) ?? "calc(100vw - 22.2rem)";

  // The jobs, with the stage and the progress of each, come from the backend (GET /api/dashboard/jobs)
  useEffect(() => {
    let cancelled = false;
    if (!companyId) return;
    const formatDaysLeft = (end: string | null) => {
      if (!end) return 0;
      const endDate = new Date(end);
      if (isNaN(endDate.getTime())) return 0;
      const todayStart = new Date();
      todayStart.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);
      return Math.max(0, Math.ceil((endDate.getTime() - todayStart.getTime()) / (1000 * 60 * 60 * 24)));
    };
    setLoading(true);
    fetchDashboardJobs()
      .then((jobs) => {
        if (cancelled) return;
        setItems(jobs.map((p) => ({
          id: p.id,
          name: p.name,
          client: p.client_name || '',
          progressPct: p.progress_pct,
          daysLeft: formatDaysLeft(p.end_date),
          budgetText: currency.format(Number(p.budget || 0)),
          location: [p.city, p.state].filter(Boolean).join(', '),
          badgeStatus: p.status,
          derivedStatus: p.derived_status,
        })));
      })
      .catch((e) => {
        if (cancelled) return;
        console.error('Error loading jobs list:', e);
        setItems([]);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [companyId, currency]);

  const filtered = useMemo(() => items.filter(i => i.derivedStatus === activeTab), [items, activeTab]);

  // Map derived status to GenericTag status type
  const getTagStatus = (derivedStatus: DerivedStatus): 'pending' | 'active' | 'completed' => {
    if (derivedStatus === 'Prospect') return 'pending';
    if (derivedStatus === 'Completed') return 'completed';
    // Estimating, In Progress, Finishing all map to 'active'
    return 'active';
  };

  const tabs = [
    { 
      value: 'Prospect', 
      label: 'Prospect',
      tooltip: 'Projects with no purchase orders, daily logs, budgets, or estimates'
    },
    { 
      value: 'Estimating', 
      label: 'Estimating',
      tooltip: 'Projects that have budgets or estimates created, but no purchase orders, bills, or invoices yet'
    },
    { 
      value: 'In Progress', 
      label: 'In Progress',
      tooltip: 'Projects which have at least one purchase order, bill, or invoice created and pending to be finished'
    },
    { 
      value: 'Finishing', 
      label: 'Finishing',
      tooltip: 'Projects where the budget is 90% or more utilized (via purchase orders) OR 90% or more of purchase orders have been received'
    },
    { 
      value: 'Completed', 
      label: 'Completed',
      tooltip: 'Projects with status set to completed'
    }
  ];

  return (
    <div className="w-full" style={{ minWidth: 0, maxWidth: '100%' }}>
      <h2 className="font-heading-text mb-4">Jobs</h2>
      <div className="mb-4">
        <GenericTabBar
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={(v) => setActiveTab(v as DerivedStatus)}
        />
      </div>
      
      <div className="w-full">
        {filtered.length === 0 ? (
          <div className="text-center font-dashboard-text-muted py-6">
            {`Currently no projects ${activeTab.toLowerCase()}`}
          </div>
        ) : (
          <div 
            className="relative w-full overflow-hidden"
            style={{ 
              maxWidth: scrollMaxWidthValue,
              width: scrollMaxWidthValue
            }}
          >
            <div 
              className="overflow-x-auto"
              style={{ width: '100%' }}
            >
              <div
                className="flex gap-4 pb-2"
                style={{
                  width: 'max-content',
                }}
              >
                {filtered.map((job) => (
                  <Card
                    key={job.id}
                    className="w-[20rem] shrink-0"
                  >
                <CardContent className="p-4">
                  <div className="space-y-4">
                    <div className="flex justify-between items-start">
                      <div>
                          <h3 className="font-subheading-text">{job.name}</h3>
                          <p className="font-dashboard-text-secondary">{job.client}</p>
                      </div>
                        <GenericTag type="status" value={getTagStatus(job.derivedStatus)} />
                    </div>
                    
                    <div>
                      <div className="flex justify-between items-center mb-2">
                          <span className="font-dashboard-text-secondary">Progress</span>
                          <span className="font-dashboard-text font-medium">{job.progressPct}%</span>
                      </div>
                      <Progress value={job.progressPct} className="h-2" />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                          <p className="font-dashboard-text-secondary">Days Left</p>
                          <p className={`font-dashboard-text font-medium ${job.daysLeft <= 7 ? 'text-red-600' : ''}`}>
                          {job.daysLeft}
                        </p>
                      </div>
                      <div>
                          <p className="font-dashboard-text-secondary">Budget</p>
                          <p className="font-dashboard-text font-medium">{job.budgetText}</p>
                      </div>
                      <div>
                          <p className="font-dashboard-text-secondary">Location</p>
                          <p className="font-dashboard-text">{job.location}</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}